import React from 'react';
import { Link } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { getNavLinkPath } from '@core/utils/routes';

export interface TabItem {
  path: string;
  title: string;
  icon?: React.ReactElement;
  disabled?: boolean;
}

interface VerticalTabsProps {
  tabs: TabItem[];
  activeTab: string;
  collapsed?: boolean;
  onItemClick?: () => void;
}

const VerticalTabs: React.FC<VerticalTabsProps> = ({
  tabs,
  activeTab,
  collapsed = false,
  onItemClick,
}) => {
  const TabComponent = ({ tab }: { tab: TabItem }) => {
    const linkPath = getNavLinkPath(tab.path);
    const active =
      activeTab === tab.path || activeTab.startsWith(`${linkPath}/`);
    return (
      <ListItem disablePadding sx={{ mb: 1 }}>
        <ListItemButton
          component={Link}
          to={linkPath}
          disabled={tab.disabled}
          onClick={onItemClick}
          sx={{
            borderRadius: '20px',
            bgcolor: active ? 'primary.main' : 'transparent',
            color: active ? 'primary.contrastText' : 'text.secondary',
            '&:hover': {
              bgcolor: active ? 'primary.dark' : 'action.hover',
            },
            '&.Mui-disabled': {
              opacity: 0.5,
              color: 'text.disabled',
            },
            minHeight: 48,
            px: collapsed ? '20px' : '12px',
          }}
        >
          {tab.icon && (
            <ListItemIcon
              sx={{
                color: 'inherit',
                minWidth: collapsed ? 'auto' : 40,
                mr: collapsed ? 0 : 1,
                justifyContent: 'center',
              }}
            >
              {tab.icon}
            </ListItemIcon>
          )}
          {!collapsed && (
            <ListItemText
              primary={tab.title}
              sx={{
                '& .MuiListItemText-primary': {
                  fontSize: (theme) => theme.typography.body2.fontSize,
                  fontWeight: active ? 600 : 400,
                },
              }}
            />
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <List sx={{ pt: 2 }}>
      {tabs.map((tab) => (
        <TabComponent key={tab.path} tab={tab} />
      ))}
    </List>
  );
};

export default VerticalTabs;
